import { formatISO, parseISO } from 'date-fns';
import ar from 'date-fns/locale/ar';
import { useTranslation } from 'next-i18next';
import * as React from 'react';
import { ReactElement, useEffect } from 'react';
import { registerLocale } from 'react-datepicker';

import 'react-datepicker/dist/react-datepicker.min.css';

import { useCreateTechnicalOperationUtils } from '@/modules/workingDay/components/CreateTechnicalOperation/logic';
import {
  Button,
  LocalizedDatePicker,
  Typography,
} from '@/shared/components/atoms';
import DemoCTA from '@/shared/components/atoms/Demo/Modals/DemoCTA';
import Search from '@/shared/components/atoms/Search';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';
import useDebounce from '@/shared/hooks/useDebounce';
import { getLocalizedDate } from '@/shared/lib/dates';

import Calendar from '~/images/shared/calender.svg';
import ArrowDown from '~/images/shared/DownArrow.svg';
import Plus from '~/images/shared/Plus.svg';

registerLocale('ar', ar);

interface IHeaderProps {
  date: string;
  setFilter: React.Dispatch<
    React.SetStateAction<{
      search: string;
      date: string;
    }>
  >;
}
export default function Header({
  date,
  setFilter,
}: IHeaderProps): ReactElement {
  const {
    t,
    i18n: { language },
  } = useTranslation('common');
  const { openModal } = useModal();
  const [search, setSearch] = React.useState('');
  const [selectedAsset] = useStore((store) => store?.asset);
  const [isDemoFarm] = useStore((store) => store?.isDemoFarm);
  const debouncedSearch = useDebounce(search, 500);

  const { openCreateTechnicalOperationModal } =
    useCreateTechnicalOperationUtils();

  useEffect(() => {
    setFilter((prev) => ({ ...prev, search }));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  return (
    <section className='flex flex-col gap-16'>
      <div className='flex w-full items-center justify-between'>
        <LocalizedDatePicker
          datePickerProps={{
            onChange: (SelectedDate) => {
              if (SelectedDate)
                setFilter((prev) => ({
                  ...prev,
                  date: formatISO(SelectedDate, { representation: 'date' }),
                }));
            },
            selected: parseISO(date),
            customInput: (
              <div className='flex h-56 cursor-pointer items-center justify-center gap-12 rounded-xl border-xs border-outline-default bg-primary-95 px-9 text-14'>
                <Calendar width='18' height='18' />
                <Typography className='text-primary-400'>
                  <span className='font-semi-bold'>{t('day')}</span>
                  {` (${getLocalizedDate({
                    date: parseISO(date),
                    locale: language,
                    formatDate: 'dd MMMM yyyy',
                  })})`}
                </Typography>
                <span className='text-primary-default'>
                  <ArrowDown />
                </span>
              </div>
            ),
          }}
        />
        {selectedAsset?.entityTypeId === 2 && (
          <Button
            onClick={() =>
              isDemoFarm
                ? openModal(<DemoCTA />)
                : openCreateTechnicalOperationModal()
            }
            size='none'
            className='flex h-56 items-center gap-8 !rounded-xl !px-16'
          >
            <>
              <Plus />
              {t('create-new-operation')}
            </>
          </Button>
        )}
      </div>
      <div className='flex justify-end'>
        <Search
          onSearch={(keyword) => {
            setSearch(keyword ?? '');
          }}
          onClick={isDemoFarm ? () => openModal(<DemoCTA />) : undefined}
          className={isDemoFarm ? 'pointer-events-none' : undefined}
        />
      </div>
    </section>
  );
}
